// sensei-app/client/ui/StudentClassroomView.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class StudentClassroomView extends ComponentBase {
    static styles = `
        .classroom-view-container {
            display: flex;
            flex-direction: column;
            height: 100vh;
            width: 100vw;
            overflow: hidden;
            position: relative;
            background-color: var(--bs-body-bg);
        }
        .classroom-main-area {
            display: flex;
            flex-direction: row;
            flex-grow: 1; /* This is crucial */
            overflow: hidden;
        }
        .ai-pane {
            flex: 1 1 50%;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-right: 1px solid var(--bs-border-color);
            transition: flex-basis 0.3s ease-in-out;
        }
        .user-pane {
            flex: 1 1 50%;
            display: flex;
            flex-direction: column; /* This makes children stack vertically */
            overflow: hidden;
            transition: flex-basis 0.3s ease-in-out;
        }
        /* When transcript is collapsed, the user pane shrinks and the AI pane grows */
        .user-pane.transcript-collapsed {
            flex-basis: 0;
            width: 0;
            flex-grow: 0; /* Stop it from growing */
            border-left: none;
            padding: 0;
            opacity: 0;
        }
        .ai-pane.transcript-collapsed {
            flex-basis: 100%;
        }
        .classroom-loading-overlay {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background-color: rgba(var(--bs-body-bg-rgb), 0.85);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            z-index: 10;
            backdrop-filter: blur(4px);
        }
    `;

    constructor(config) {
        super(config);
        this.isTranscriptVisible = false;
        this.scheduleId = null;
        this.sessionId = null;
        this.sessionStartTime = null;
        this.isLoading = true;
        this.isEnding = false;
        this.toolbar = null;
        this.whiteboard = null;
        this.inputArea = null;
        this.transcript = null;
        this.turnManager = null;
        this.currentWhiteboardContent = "";

        this.inactivityTimer = null;
        this.INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
        this._boundResetInactivityTimer = this.resetInactivityTimer.bind(this);

        this.applyStyles();
    }

    startInactivityTimer() {
        this.stopInactivityTimer(); // Clear any existing timer
        this.inactivityTimer = setTimeout(() => {
            this.log.info("User has been inactive. Notifying server.");
            this.appClient.rpcClient.call('StudentClassroomRpc.notifyInactivity', { sessionId: this.sessionId })
                .catch(err => this.log.error("Failed to notify server of inactivity:", err));
        }, this.INACTIVITY_TIMEOUT_MS);
    }

    stopInactivityTimer() {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }

    resetInactivityTimer() {
        this.startInactivityTimer();
    }

    async initialize() {
        await super.initialize();
        this.container = document.createElement('div');
        this.container.className = 'classroom-view-container';

        this.toolbar = await this.createManagedComponent('ClassroomToolbar', null, { instanceId: `${this.config.instanceId}-toolbar` });
        this.whiteboard = await this.createManagedComponent('ClassroomWhiteboard', null, { instanceId: `${this.config.instanceId}-whiteboard` });
        this.turnManager = await this.createManagedComponent('ClassroomTurnManager', null, { instanceId: `${this.config.instanceId}-turnmanager` });
        this.inputArea = await this.createManagedComponent('ClassroomInputArea', null, { instanceId: `${this.config.instanceId}-input` });
        this.transcript = await this.createManagedComponent('ClassroomTranscript', null, { instanceId: `${this.config.instanceId}-transcript` });

        this.toolbar.on('endClassClicked', this.handleEndClass.bind(this));
        this.toolbar.on('pauseClassClicked', this.handlePauseClass.bind(this));
        this.toolbar.on('raiseHandClicked', this.handleRaiseHand.bind(this));
        this.inputArea.on('studentInputSent', this.handleStudentInput.bind(this));
        this.turnManager.on('advanceTurn', () => this.handleStudentInput({ text: '[USER_ADVANCED_TURN]' }));
        this.transcript.on('toggleVisibility', () => {
            this.isTranscriptVisible = !this.isTranscriptVisible;
            this.render();
            setTimeout(() => this.inputArea.handleResize(), 50);
        });

        this._boundHandleAgentResponse = this.handleAgentResponse.bind(this);
        this.appClient.events.server.on('agentResponse', this._boundHandleAgentResponse);

        window.addEventListener('mousemove', this._boundResetInactivityTimer);
        window.addEventListener('keydown', this._boundResetInactivityTimer);
        window.addEventListener('click', this._boundResetInactivityTimer);
    }



    async handleStudentInput(inputPayload) {
        if (!this.sessionId) return;

        // Optimistically add student's message to transcript
        // Don't add system messages like [USER_ADVANCED_TURN] to the transcript
        if (inputPayload.text !== '[USER_ADVANCED_TURN]') {
            this.transcript.addMessage({
                sender: 'student',
                content: inputPayload.text,
                inputThumbnail: inputPayload.image
            });
        }

        try {
            await this.appClient.rpcClient.call('StudentClassroomRpc.sendStudentInput', {
                sessionId: this.sessionId,
                input: inputPayload
            });
        } catch (error) {
            this.log.error("Error sending student input:", error);
            this.transcript.addMessage({ sender: 'error', content: 'Could not send your message.' });
        }
    }

    handleAgentResponse(payload) {
        if (payload.agentName !== 'ClassroomAgent' || payload.agentInstanceId !== this.sessionId) {
            return;
        }

        switch (payload.type) {
            case 'message':
                if (payload.message && payload.message.content) {
                    const messageContent = payload.message.content;
                    this.transcript.addMessage({ sender: 'ai', content: messageContent });
                    this.whiteboard.setContent({ contentType: 'markdown', content: messageContent });
                }
                break;
            case 'message_delta':
                if (payload.delta) {
                    this.transcript.streamMessageChunk(payload.delta);
                    this.whiteboard.setContent({ contentType: 'markdown', content: this.transcript.getStreamingBuffer() });
                }
                break;
            case 'message_done':
                const finalContent = this.transcript.finalizeStreamedMessage();
                const turnTokenRegex = /\[AWAIT_NEXT_TURN(?::(\d+))?\]\s*$/;
                const match = finalContent.match(turnTokenRegex);

                if (match) {
                    const duration = match[1] ? parseInt(match[1], 10) : 30;
                    const cleanContent = finalContent.replace(turnTokenRegex, '').trim();

                    this.transcript.updateLastMessageContent(cleanContent);
                    this.whiteboard.setContent({ contentType: 'markdown', content: cleanContent });

                    this.turnManager.start(duration);
                } else {
                    // --- THIS IS THE FIX ---
                    // If no token is found, explicitly stop and hide the turn manager.
                    this.turnManager.stop();
                    // --- END FIX ---
                }
                break;
            case 'error':
                this.transcript.addMessage({ sender: 'error', content: payload.error });
                break;
            case 'status':
                this.log.debug(`Agent status: ${payload.status}`);
                break;
        }
    }


    async startSession() {
        this.isLoading = true;
        this.isEnding = false;
        this.render();

        try {
            const result = await this.appClient.rpcClient.call('StudentClassroomRpc.startClassSession', { scheduleId: this.scheduleId });

            if (result && result.sessionId) {
                this.sessionId = result.sessionId;
                this.sessionStartTime = new Date();
                this.isLoading = false;
                this.startInactivityTimer(); // NEW: Start the timer when session begins

                this.render();

                if (result.initialMessage) {
                    const finalContent = result.initialMessage.content || "";
                    const turnTokenRegex = /\[AWAIT_NEXT_TURN(?::(\d+))?\]\s*$/;
                    const match = finalContent.match(turnTokenRegex);

                    if (match) {
                        const duration = match[1] ? parseInt(match[1], 10) : 30;
                        const cleanContent = finalContent.replace(turnTokenRegex, '').trim();
                        result.initialMessage.content = cleanContent;
                        this.transcript.addMessage(result.initialMessage);
                        this.whiteboard.setContent({ contentType: 'markdown', content: cleanContent });
                        this.turnManager.start(duration);
                    } else {
                        this.transcript.addMessage(result.initialMessage);
                        this.whiteboard.setContent({ contentType: 'markdown', content: finalContent });
                        this.turnManager.stop();
                    }
                }
            } else {
                throw new Error("Failed to retrieve a session ID from the server.");
            }
        } catch (error) {
            this.log.error("Error starting class session:", error);
            this.showError(`Could not start the class session: ${error.message}`);
        }
    }


    render() {
        if (!this.container) return;
        this.container.innerHTML = '';

        if (this.isLoading || this.isEnding) {
            const loaderText = this.isEnding ? 'Ending class session...' : 'Starting class session...';
            this.container.innerHTML = `
            <div class="classroom-loading-overlay">
                <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status"></div>
                <h5 class="text-muted">${this.t(loaderText)}</h5>
            </div>`;
            this.appClient.tx.applyTranslations(this.container);
            return this.container;
        }

        this.toolbar.startTime = this.sessionStartTime;
        this.container.appendChild(this.toolbar.render());

        const mainArea = document.createElement('div');
        mainArea.className = 'classroom-main-area';

        const aiPane = document.createElement('div');
        aiPane.className = 'ai-pane';
        aiPane.classList.toggle('transcript-collapsed', !this.isTranscriptVisible);
        aiPane.appendChild(this.whiteboard.render());
        aiPane.appendChild(this.turnManager.render()); // NEW: Add turn manager to AI pane
        mainArea.appendChild(aiPane);

        const userPane = document.createElement('div');
        userPane.className = 'user-pane';
        this.transcript.isCollapsed = !this.isTranscriptVisible;
        userPane.appendChild(this.transcript.render());
        userPane.appendChild(this.inputArea.render());
        mainArea.appendChild(userPane);

        this.container.appendChild(mainArea);
        return this.container;
    }

    async handlePauseClass() {
        const confirmed = await this.appClient.notifier.confirm(
            this.t("Are you sure you want to pause this session? You can resume it later from your schedule."),
            { title: this.t("Pause Session"), type: 'info' }
        );
        if (confirmed && this.sessionId) {
            try {
                await this.appClient.rpcClient.call('StudentClassroomRpc.pauseClassSession', { sessionId: this.sessionId });
                this.appClient.notifier.success("Session paused. You can resume it from your schedule.");
                // Navigate away from the classroom after pausing
                this.appClient.layoutEngine.navigate('/student/schedule');
            } catch (error) {
                this.log.error("Error pausing class session:", error);
                this.appClient.notifier.error("Could not pause the session.");
            }
        }
    }

    async refresh() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        this.scheduleId = queryParams.scheduleId;
        if (!this.scheduleId) {
            this.showError("No class schedule specified.");
            return;
        }
        await this.startSession();
    }

    async handleEndClass() {
        const confirmed = await this.appClient.notifier.confirm("Are you sure you want to end this class session?", { type: 'warning' });
        if (confirmed && this.sessionId) {
            this.isEnding = true;
            this.render(); // Show the "Ending session..." overlay

            const sessionIdToEnd = this.sessionId;
            this.sessionId = null; // Invalidate the session ID locally
            this.stopInactivityTimer(); // Stop the inactivity timer
            this.turnManager.stop(); // Stop the turn manager timer

            try {
                // Now, make the RPC call with the saved session ID
                await this.appClient.rpcClient.call('StudentClassroomRpc.endClassSession', { sessionId: sessionIdToEnd });

                this.appClient.notifier.success("Class session ended.");
                // Navigate away after the server has confirmed the action
                this.appClient.layoutEngine.navigate('/student/schedule');
            } catch (error) {
                this.log.error("Error ending class session:", error);
                this.appClient.notifier.error("Could not end the session.");
                // If the server call fails, we are already in a safe state because the local session is invalidated.
                // We can either try to restore it or just navigate away. Navigating away is safer.
                this.isEnding = false;
                this.appClient.layoutEngine.navigate('/student/schedule');
            }
        }
    }



    showError(message) {
        this.isLoading = false;
        this.isEnding = false;
        if (this.container) {
            this.container.innerHTML = `
                <div class="alert alert-danger m-3">
                    <h4>Error</h4>
                    <p>${this.t(message)}</p>
                    <a href="/student/schedule" class="btn btn-primary">${this.t('Back to Schedule')}</a>
                </div>
            `;
            this.appClient.tx.applyTranslations(this.container);
        }
    }

    handleRaiseHand() {
        this.log.info("Raise Hand clicked.");
        this.handleStudentInput({ text: '[Student raised hand to ask a question]' });
    }

    destroy() {
        this.appClient.events.server.off('agentResponse', this._boundHandleAgentResponse);
        this.toolbar?.destroy();
        this.whiteboard?.destroy();
        this.turnManager?.destroy();
        this.inputArea?.destroy();
        this.transcript?.destroy();

        this.stopInactivityTimer();
        window.removeEventListener('mousemove', this._boundResetInactivityTimer);
        window.removeEventListener('keydown', this._boundResetInactivityTimer);
        window.removeEventListener('click', this._boundResetInactivityTimer);

        super.destroy();
    }

}